import { useMemo } from 'react';
import type { QueryObserverResult } from '@tanstack/react-query';
import { AccessInput } from '@/abilities/accessInput';

/**
 * A query as an access input. The same object comes back while the query's status, answer and error stay the same:
 * the cache keeps the answer's object while its content is unchanged, and a reader above depends on that object.
 * Only the status decides. A refetch in progress keeps the earlier answer known; a failed refetch is a failure
 * although TanStack Query keeps the earlier answer — access is never decided on an answer whose refresh failed.
 */
export const useAccessInputFromQuery = <Value>(query: QueryObserverResult<Value, unknown>): AccessInput<Value> => {
  const { status, data, error } = query;
  return useMemo(() => {
    if (status === 'success') return AccessInput.Ready({ value: data });
    if (status === 'error') return AccessInput.Failed({ error });
    return AccessInput.Loading();
  }, [status, data, error]);
};
